import React, { useState, useEffect, useCallback } from 'react';
import { Menu } from 'lucide-react';
import { Toaster } from 'react-hot-toast';
import CandidateCards from './components/CandidateCards';
import CandidateDetail from './components/CandidateDetail';
import AddCandidateModal from './components/AddCandidateModal';
import Sidebar, { NavModule } from './components/Sidebar';
import LoginPage from './components/LoginPage';
import TeamManager from './components/TeamManager';
import SystemSettings from './components/SystemSettings';
import FeishuNotifyModal from './components/FeishuNotifyModal';
import BatchImportModal from './components/BatchImportModal';
import NeedsList from './components/NeedsList';
import NeedDetail from './components/NeedDetail';
import NotificationCenter from './components/NotificationCenter';
import CreateNeedModal from './components/CreateNeedModal';
import HomePage from './components/HomePage';
import PositionManager from './components/PositionManager';
import OnboardingManager from './components/OnboardingManager';
import OfferApprovalPage from './components/OfferApprovalPage';
import TalentHub from './components/TalentHub';
import BossBrief from './components/BossBrief';
import ReferralPage from './components/ReferralPage';
import ReferralDashboard from './components/ReferralDashboard';
import DashboardPage from './components/DashboardPage';
import ItResourcePage from './components/ItResourcePage';
import CareerPage from './components/CareerPage';
import WebsiteApplicants from './components/WebsiteApplicants';
import CandidateShareView from './components/CandidateShareView';
import HeadhunterPage from './components/HeadhunterPage';
import ReferralPosterPage from './components/ReferralPosterPage';
import { getCandidates, getPositions, getTags, getMe, logout as apiLogout } from './api';
import { Candidate, Position, Tag, User } from './types';
import './index.css';

function App() {
  // Public career page - no auth needed, render before any hooks
  const isCareerPage = window.location.pathname.startsWith('/career/');
  if (isCareerPage) return <CareerPage />;

  // Public headhunter page - token auth
  const isHeadhunterPage = window.location.pathname.startsWith('/headhunter/');
  if (isHeadhunterPage) return <HeadhunterPage />;

  // Public candidate share page - no auth needed
  const isSharePage = window.location.pathname.startsWith('/share/');
  if (isSharePage) return <CandidateShareView />;

  // Public referral poster page - employee self-service
  const isReferralPoster = window.location.pathname.startsWith('/referral-poster');
  if (isReferralPoster) return <ReferralPosterPage />;

  return <MainApp />;
}

function MainApp() {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedPositionId, setSelectedPositionId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTeamManager, setShowTeamManager] = useState(false);
  const [feishuNotifyCandidates, setFeishuNotifyCandidates] = useState<Candidate[] | null>(null);
  const [batchImportPositionId, setBatchImportPositionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [totalCandidateCount, setTotalCandidateCount] = useState<number>(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Navigation
  const [activeModule, setActiveModule] = useState<NavModule>('home');
  const [selectedNeedId, setSelectedNeedId] = useState<string | null>(null);
  const [showCreateNeed, setShowCreateNeed] = useState(false);
  const [needsRefreshKey, setNeedsRefreshKey] = useState(0);
  const [currentStageCandidateIds, setCurrentStageCandidateIds] = useState<string[]>([]);
  const [homeCandidateIds, setHomeCandidateIds] = useState<string[]>([]);
  const [pinnedPositionIds, setPinnedPositionIds] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('pinned_positions') || '[]'); } catch { return []; }
  });

  const togglePinPosition = (positionId: string) => {
    setPinnedPositionIds(prev => {
      const next = prev.includes(positionId) ? prev.filter(id => id !== positionId) : [...prev, positionId];
      localStorage.setItem('pinned_positions', JSON.stringify(next));
      return next;
    });
  };
  const [homeRefreshKey, setHomeRefreshKey] = useState(0);

  // Check auth on mount
  useEffect(() => {
    const token = localStorage.getItem('ats_token');
    if (token) {
      getMe().then(user => {
        setCurrentUser(user);
        setAuthChecked(true);
      }).catch(() => {
        localStorage.removeItem('ats_token');
        localStorage.removeItem('ats_user');
        setAuthChecked(true);
      });
    } else {
      setAuthChecked(true);
    }
  }, []);

  const handleLogin = (user: User, token: string) => {
    setCurrentUser(user);
    // Enrich with effective permissions + position assignments
    getMe().then(full => setCurrentUser(full)).catch(() => {});
  };

  const handleLogout = async () => {
    try { await apiLogout(); } catch {}
    localStorage.removeItem('ats_token');
    localStorage.removeItem('ats_user');
    setCurrentUser(null);
    setCandidates([]);
    setPositions([]);
    setTags([]);
    setSelectedId(null);
  };

  const fetchCandidates = useCallback(async () => {
    try {
      const params: Record<string, string> = { limit: '10000' };
      if (selectedPositionId) params.position_id = selectedPositionId;
      if (searchQuery) params.search = searchQuery;
      if (stageFilter) params.stage = stageFilter;
      if (statusFilter) params.status = statusFilter;
      const data = await getCandidates(params);
      setCandidates(data.candidates);
      // Track total count when viewing talent pool (no position filter)
      if (!selectedPositionId && !searchQuery && !stageFilter && !statusFilter) {
        setTotalCandidateCount(data.pagination?.total ?? data.candidates.length);
      }
    } catch (err) {
      console.error('Failed to fetch candidates:', err);
    }
  }, [selectedPositionId, searchQuery, stageFilter, statusFilter]);

  const fetchPositions = useCallback(async () => {
    try {
      const data = await getPositions();
      setPositions(data);
    } catch (err) {
      console.error('Failed to fetch positions:', err);
    }
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const data = await getTags();
      setTags(data);
    } catch (err) {
      console.error('Failed to fetch tags:', err);
    }
  }, []);

  useEffect(() => {
    if (currentUser) {
      Promise.all([fetchPositions(), fetchTags()]).then(() => setLoading(false));
      if (currentUser.role === 'referrer') setActiveModule('referral');
      if (currentUser.role === 'it') setActiveModule('it_resources');
    }
  }, [currentUser, fetchPositions, fetchTags]);

  useEffect(() => {
    if (!loading && currentUser) fetchCandidates();
  }, [fetchCandidates, loading, currentUser]);

  // Handle URL params: ?candidate=xxx opens candidate detail directly (from Feishu links)
  useEffect(() => {
    if (!loading && currentUser) {
      const params = new URLSearchParams(window.location.search);
      const candidateId = params.get('candidate');
      if (candidateId) {
        setActiveModule('candidates');
        setSelectedId(candidateId);
        // Clean URL without reload
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, [loading, currentUser]);

  // Auth check spinner
  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent"></div>
      </div>
    );
  }

  // Not logged in
  if (!currentUser) {
    return (
      <>
        <LoginPage onLogin={handleLogin} />
        <Toaster position="top-right" toastOptions={{ duration: 3000, style: { background: '#363636', color: '#fff', fontSize: '14px' } }} />
      </>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar: fixed drawer on mobile, static on desktop */}
      <div className={`
        fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 ease-out md:relative md:translate-x-0 md:z-auto
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <Sidebar
          activeModule={activeModule}
          onModuleChange={(m) => { setActiveModule(m); setSelectedId(null); setSelectedNeedId(null); setSidebarOpen(false); }}
          currentUser={currentUser}
          onLogout={handleLogout}
          onOpenTeamManager={() => { if (currentUser.role === 'admin') setShowTeamManager(true); setSidebarOpen(false); }}
          positions={positions}
          selectedPositionId={selectedPositionId}
          onPositionChange={(id) => { setSelectedPositionId(id); setSelectedId(null); }}
          totalCandidateCount={totalCandidateCount}
          onAddCandidate={() => setShowAddModal(true)}
          onBatchImport={(positionId) => setBatchImportPositionId(positionId)}
          pinnedPositionIds={pinnedPositionIds}
          onTogglePinPosition={togglePinPosition}
          onOpenWebsiteApplicants={() => { setActiveModule('website_applicants'); setSelectedPositionId(''); setSelectedId(null); setSidebarOpen(false); }}
        />
      </div>

      {/* Right Main Area */}
      <main className="flex-1 overflow-hidden relative">
        {/* Mobile hamburger */}
        <button
          onClick={() => setSidebarOpen(true)}
          className="md:hidden fixed top-3 left-3 z-30 p-2 rounded-lg bg-white shadow-md border border-gray-200 text-gray-600 hover:bg-gray-50"
        >
          <Menu size={20} />
        </button>

        {(() => {
          const bell = (
            <NotificationCenter
              onNavigate={(link) => {
                if (link.startsWith('?candidate=')) {
                  const cid = link.replace('?candidate=', '');
                  setActiveModule('candidates');
                  setSelectedId(cid);
                } else if (link.startsWith('?need=')) {
                  const nid = link.replace('?need=', '');
                  setActiveModule('needs');
                  setSelectedNeedId(nid);
                }
              }}
            />
          );
          return (
            <>
              {activeModule === 'home' && (
                <HomePage
                  currentUser={currentUser}
                  notificationBell={bell}
                  onNavigateCandidate={(id) => { setSelectedId(id); }}
                  onOpenCandidate={(id, cIds) => { setSelectedId(id); if (cIds) setHomeCandidateIds(cIds); }}
                  onNavigateToOnboarding={() => setActiveModule('onboarding')}
                  refreshKey={homeRefreshKey}
                />
              )}

              {activeModule === 'candidates' && (
                <CandidateCards
                  candidates={candidates}
                  onSelect={setSelectedId}
                  positionName={positions.find(p => p.id === selectedPositionId)?.name}
                  currentPositionId={selectedPositionId || undefined}
                  isHr={currentUser.role === 'admin' || currentUser.role === 'hr' || currentUser.role === 'vip'}
                  onFeishuNotify={(selected: Candidate[]) => setFeishuNotifyCandidates(selected)}
                  onUpdate={() => { fetchCandidates(); fetchPositions(); }}
                  notificationBell={bell}
                  onStageChange={setCurrentStageCandidateIds}
                  positions={positions}
                  searchQuery={searchQuery}
                  onSearch={(q) => { setSearchQuery(q); }}
                />
              )}

              {activeModule === 'needs' && !selectedNeedId && (
                <NeedsList
                  currentUser={currentUser}
                  onSelect={(id) => setSelectedNeedId(id)}
                  onCreateNew={() => setShowCreateNeed(true)}
                  refreshKey={needsRefreshKey}
                  notificationBell={bell}
                />
              )}

              {activeModule === 'needs' && selectedNeedId && (
                <NeedDetail
                  needId={selectedNeedId}
                  currentUser={currentUser}
                  onBack={() => setSelectedNeedId(null)}
                  onUpdate={() => { setNeedsRefreshKey(k => k + 1); fetchPositions(); }}
                  notificationBell={bell}
                />
              )}

              {activeModule === 'positions' && (
                <PositionManager
                  positions={positions}
                  onUpdate={fetchPositions}
                  currentUser={currentUser}
                  notificationBell={bell}
                />
              )}

              {activeModule === 'offer_approval' && (
                <OfferApprovalPage
                  currentUser={currentUser}
                  notificationBell={bell}
                  onOpenCandidate={(id) => setSelectedId(id)}
                />
              )}

              {activeModule === 'onboarding' && (
                <OnboardingManager
                  currentUser={currentUser}
                  notificationBell={bell}
                  onOpenCandidate={(id) => setSelectedId(id)}
                />
              )}

              {activeModule === 'it_resources' && (
                <ItResourcePage currentUser={currentUser} notificationBell={bell} />
              )}

              {activeModule === 'referral' && (
                ['admin', 'hr', 'vip'].includes(currentUser.role)
                  ? <ReferralDashboard currentUser={currentUser} notificationBell={bell} onOpenCandidate={(id) => setSelectedId(id)} />
                  : <ReferralPage currentUser={currentUser} notificationBell={bell} onOpenCandidate={(id) => setSelectedId(id)} />
              )}

              {activeModule === 'boss_brief' && (
                <BossBrief currentUser={currentUser} notificationBell={bell} onOpenCandidate={(id) => setSelectedId(id)} />
              )}

              {activeModule === 'talent_hub' && (
                <TalentHub currentUser={currentUser} notificationBell={bell} />
              )}

              {activeModule === 'website_applicants' && (
                <WebsiteApplicants
                  currentUser={currentUser}
                  notificationBell={bell}
                  onOpenCandidate={(id) => { setActiveModule('candidates'); setSelectedId(id); }}
                />
              )}

              {activeModule === 'dashboard' && (
                <DashboardPage currentUser={currentUser} notificationBell={bell} />
              )}

              {activeModule === 'team_management' && currentUser.role === 'admin' && (
                <TeamManager
                  currentUser={currentUser}
                  positions={positions}
                  embedded
                />
              )}

              {activeModule === 'system_settings' && currentUser.role === 'admin' && (
                <SystemSettings currentUser={currentUser} />
              )}
            </>
          );
        })()}
      </main>

      {/* Candidate Detail Modal Overlay */}
      {selectedId && (
        <CandidateDetail
          candidateId={selectedId}
          positions={positions}
          allTags={tags}
          currentUser={currentUser}
          onUpdate={() => { fetchCandidates(); }}
          onTagsChange={fetchTags}
          onBack={() => { setSelectedId(null); if (activeModule === 'home') setHomeRefreshKey(k => k + 1); }}
          candidateIds={activeModule === 'home' && homeCandidateIds.length > 0 ? homeCandidateIds : currentStageCandidateIds.length > 0 ? currentStageCandidateIds : candidates.map(c => c.id)}
          onNavigate={(id) => setSelectedId(id)}
        />
      )}

      {showAddModal && (
        <AddCandidateModal
          positions={positions}
          onClose={() => setShowAddModal(false)}
          onCreated={() => { setShowAddModal(false); fetchCandidates(); }}
        />
      )}

      {showTeamManager && currentUser.role === 'admin' && (
        <TeamManager
          currentUser={currentUser}
          positions={positions}
          onClose={() => setShowTeamManager(false)}
        />
      )}

      {batchImportPositionId && (
        <BatchImportModal
          positionId={batchImportPositionId}
          positionName={positions.find(p => p.id === batchImportPositionId)?.name || '未知岗位'}
          positions={positions.map(p => ({ id: p.id, name: p.name }))}
          onClose={() => setBatchImportPositionId(null)}
          onImported={() => { fetchCandidates(); fetchPositions(); }}
        />
      )}

      {feishuNotifyCandidates && (
        <FeishuNotifyModal
          candidates={feishuNotifyCandidates}
          autoAssignPositionId={selectedPositionId || undefined}
          autoAssignPositionName={positions.find(p => p.id === selectedPositionId)?.name}
          onClose={() => setFeishuNotifyCandidates(null)}
          onSent={() => { setFeishuNotifyCandidates(null); fetchCandidates(); fetchPositions(); }}
        />
      )}

      {showCreateNeed && (
        <CreateNeedModal
          currentUser={currentUser}
          onClose={() => setShowCreateNeed(false)}
          onCreated={(id) => { setShowCreateNeed(false); setSelectedNeedId(id); setNeedsRefreshKey(k => k + 1); }}
        />
      )}

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: { background: '#363636', color: '#fff', fontSize: '14px' },
        }}
      />
    </div>
  );
}

export default App;
